import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Redirect } from "react-router-dom";

import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import SaveIcon from "@material-ui/icons/Save";
import DeleteIcon from "@material-ui/icons/Delete";

import ScrollableTabs from "@meetyoo/components/src/components/Tabs/ScrollableTabs";

import { PM_CONFERENCES_PATH } from "../../../constants/router";

import PMConference from "../PMConference";
import PMBillingData from "../PMBillingData";
import PMHostAndGuest from "../PMHostAndGuest";
import PMGeneralOptions from "../PMGeneralOptions";
import PMPrepaidAndAlert from "../PMPrepaidAndAlert";
import PMEntry from "../PMEntry";
import PMInternalInfo from "../PMInternalInfo";

import { difference } from "../../../utils/objectMethods";

import { requestConferences, updateConference, requestParams, updatePin } from "../../../actions/passcodeManager";

const useStyles = makeStyles(theme => ({
  button: {
    margin: theme.spacing(2, 1, 1, 1),
    width: 100,
  },
  backButton: {
    height: theme.spacing(4),
    margin: theme.spacing(1),
  },
  spaceButtons: {
    flex: "1",
    width: "10px",
  },
  inLine: {
    width: "100%",
    display: "inline-flex",
  },
}));

const customStyle = theme => ({
  Box: {
    padding: theme.spacing(0),
  },
  appBar: {
    boxShadow: "none",
  },
});

const confRestRepInit = {
  owner: "",
  product: "",
  hostPasscode: "",
  guestPasscode: "",
  unattendedDialoutAllowed: false,
  unattendedDialoutGuestAllowed: false,
};

export const PMTabsComponent = ({
  conferences,
  selectValues,
  match,
  filter,
  username,
  translate,
  getConferencesHanlder,
  updateConferenceHandler,
  requestSelectValues,
  errorMessage,
  updatePinHandler,
}) => {
  const [confOriginal, setConfOriginal] = useState(confRestRepInit);
  const [confRestRep, setConfRestRep] = useState(confRestRepInit);
  const [updateRequest, setUpdateRequest] = useState(false);
  const id = Number(match.params.id);
  const classes = useStyles();

  useEffect(() => {
    const conf = conferences.filter(c => c.conferenceId === id)[0] || confRestRepInit;
    if (conferences.length === 0) getConferencesHanlder(filter);
    else if (conf.conferenceId === id && confRestRep.guestPasscode !== conf.guestPasscode) {
      setConfRestRep({
        ...confRestRep,
        guestPasscode: conf.guestPasscode,
        hostPasscode: conf.hostPasscode,
      });
      setConfOriginal({
        ...confOriginal,
        guestPasscode: conf.guestPasscode,
        hostPasscode: conf.hostPasscode,
      });
    } else if (confOriginal.conferenceId !== id && confOriginal !== confRestRepInit) {
      setConfRestRep(conf);
      setConfOriginal(conf);
    }
    if (Object.keys(selectValues).length === 0) requestSelectValues();
  }, [
    conferences,
    getConferencesHanlder,
    confOriginal,
    confRestRep,
    id,
    requestSelectValues,
    selectValues,
    filter,
    setConfOriginal,
    setConfRestRep,
  ]);

  return updateRequest || errorMessage !== "" ? (
    <Redirect to={PM_CONFERENCES_PATH} />
  ) : (
    [
      <div className={classes.inLine} key="Tabs">
        {Object.keys(selectValues).length > 0 ? (
          <ScrollableTabs
            key={`pm_tabs_${confRestRep.conferenceId}`}
            customStyle={customStyle}
            tabs={[
              {
                title: translate.conferenceTab,
                component: (
                  <PMConference
                    translate={translate}
                    confRestRep={confRestRep}
                    setConfRestRep={setConfRestRep}
                    selectValues={selectValues}
                    requestNewPin={updatePinHandler}
                  />
                ),
              },
              {
                title: translate.billingDataTab,
                component: (
                  <PMBillingData
                    translate={translate}
                    confRestRep={confRestRep}
                    setConfRestRep={setConfRestRep}
                    selectValues={selectValues}
                  />
                ),
              },
              {
                title: translate.hostGuestTab,
                component: (
                  <PMHostAndGuest
                    translate={translate}
                    confRestRep={confRestRep}
                    setConfRestRep={setConfRestRep}
                    selectValues={selectValues}
                  />
                ),
              },
              {
                title: translate.generelOptionsTab,
                component: (
                  <PMGeneralOptions
                    translate={translate}
                    confRestRep={confRestRep}
                    setConfRestRep={setConfRestRep}
                    selectValues={selectValues}
                  />
                ),
              },
              {
                title: translate.alertPrepaidTab,
                component: (
                  <PMPrepaidAndAlert
                    translate={translate}
                    confRestRep={confRestRep}
                    setConfRestRep={setConfRestRep}
                    selectValues={selectValues}
                  />
                ),
              },
              {
                title: translate.entryTab,
                component: (
                  <PMEntry
                    translate={translate}
                    confRestRep={confRestRep}
                    setConfRestRep={setConfRestRep}
                    selectValues={selectValues}
                  />
                ),
              },
              {
                title: translate.internalInfoTab,
                component: <PMInternalInfo translate={translate} conference={confRestRep} />,
              },
            ]}
          />
        ) : null}
      </div>,
      <div className={classes.inLine} key="Buttons">
        <Button
          key="pm_tabs_btn_save"
          variant="contained"
          size="small"
          color={confOriginal === confRestRep ? "default" : "primary"}
          className={classes.button}
          onClick={() => {
            if (Object.keys(difference(confRestRep, confOriginal)).length !== 0) {
              updateConferenceHandler(id, { ...difference(confRestRep, confOriginal), changedBy: username });
              setUpdateRequest(true);
            }
          }}
        >
          <SaveIcon className={clsx(classes.leftIcon, classes.iconSmall)} />
          {translate.saveButton}
        </Button>
        <div className={classes.spaceButtons} />
        <Button
          onClick={() => {
            // TODO
          }}
          key="pm_tabs_btn_del"
          variant="contained"
          color="secondary"
          className={classes.button}
        >
          {translate.deleteButton}
          <DeleteIcon className={classes.rightIcon} />
        </Button>
      </div>,
    ]
  );
};

PMTabsComponent.propTypes = {
  translate: PropTypes.object.isRequired,
  conferences: PropTypes.array.isRequired,
  match: PropTypes.object.isRequired,
  getConferencesHanlder: PropTypes.func.isRequired,
  updateConferenceHandler: PropTypes.func.isRequired,
  selectValues: PropTypes.object.isRequired,
  requestSelectValues: PropTypes.func.isRequired,
  filter: PropTypes.object.isRequired,
  username: PropTypes.string.isRequired,
  errorMessage: PropTypes.string.isRequired,
  updatePinHandler: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  conferences: state.passcodeManager.conferences,
  selectValues: state.passcodeManager.selectValues,
  filter: state.passcodeManager.filter,
  username: state.oauth.username,
  errorMessage: state.passcodeManager.errorMessage,
  translate: state.ui.translate,
});

const mapDispatchToProps = dispatch => ({
  getConferencesHanlder: filter => dispatch(requestConferences(filter)),
  requestSelectValues: () => dispatch(requestParams()),
  updateConferenceHandler: (id, conference) => dispatch(updateConference(id, conference)),
  updatePinHandler: (id, type, confRestRep) => dispatch(updatePin(id, type, confRestRep)),
});

const PMTabs = connect(
  mapStateToProps,
  mapDispatchToProps
)(PMTabsComponent);

export default PMTabs;
